import { Error, Info, Warning } from '@mui/icons-material';
import { Grid, lighten, Typography, useTheme } from '@mui/material';

import { useCustomizations } from 'src/components/utils/theme/customizations';
import { ThemeType } from 'src/components/utils/theme/ThemeType';

type InfoType = 'information' | 'warning' | 'error';

type InfoBoxProps = {
    text: string;
    infoType: InfoType;
    compact?: boolean;
};

export default function InfoBox({ text, infoType, compact = false }: InfoBoxProps) {
    const customizations = useCustomizations();
    const theme = useTheme();
    const icon = getIcon(infoType, customizations, compact);

    return (
        <Grid
            container
            width="100%"
            p={compact ? theme.spacing(2, 2, 2, 2) : theme.spacing(4, 4, 4, 3)}
            justifyContent="flex-start"
            alignItems="center"
            borderRadius={`${customizations.borderRadius}px`}
            sx={getColor(infoType, customizations)}
            wrap="nowrap"
        >
            <Grid justifyContent="center" mr={theme.spacing(compact ? 2 : 3)}>
                {icon}
            </Grid>
            <Typography variant={compact ? 'body2' : 'body1'}>{text}</Typography>
        </Grid>
    );
}

function getColor(infoType: InfoType, customizations: ThemeType) {
    switch (infoType) {
        case 'information':
            return {
                backgroundColor: lighten(customizations.informationColor, 0.8),
                border: `1px ${customizations.informationColor} solid`,
            };
        case 'warning':
            return {
                backgroundColor: lighten(customizations.warningColor, 0.8),
                border: `1px ${customizations.warningColor} solid`,
            };
        case 'error':
            return {
                backgroundColor: lighten(customizations.errorColor, 0.8),
                border: `1px ${customizations.errorColor} solid`,
            };
    }
}

function getIcon(infoType: InfoType, customizations: ThemeType, compact: boolean) {
    const fontSize = compact ? 'small' : 'large';
    switch (infoType) {
        case 'information':
            return <Info fontSize={fontSize} style={{ color: customizations.bodyColor }} />;
        case 'warning':
            return <Warning fontSize={fontSize} style={{ color: customizations.bodyColor }} />;
        case 'error':
            return <Error fontSize={fontSize} style={{ color: customizations.bodyColor }} />;
    }
}
